import React, { useState, useEffect } from 'react';
import { Phone, Menu, X } from "lucide-react";
import { useLocation } from 'react-router-dom';

const navLinks = [
  { label: "Home",          id: "home" },
  { label: "Portfolio",     id: "projects" },
  { label: "Services",      id: "services" },
  { label: "Service Areas", id: "areas" },
  { label: "About",         id: "about" },
  { label: "Contact",       id: "contact" },
];

export default function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const routes = { home: '/', projects: '/projects', services: '/services', areas: '/service-areas', about: '/#about', contact: '/request-a-quote' };
  const navHref = id => pathname === '/' && id !== 'home' ? `/#${id}` : routes[id];
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const sectionIds = navLinks.map(l => l.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);


  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070B10]/92 backdrop-blur-xl border-b border-white/8 shadow-[0_10px_35px_rgba(0,0,0,0.28)] w-full max-w-[100vw] overflow-x-hidden">
        <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2.5" style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top))' }}>
          <div className="flex items-center justify-between gap-6 w-full">
            {/* Logo */}
            <a href="/" aria-label="Oliver and Sons Plumbing home"><img
              src="/images/optimized/performance/logo-220.webp"
              alt="Oliver & Sons Plumbing"
              width="220"
              height="220"
              className="w-[120px] md:w-[150px] lg:w-[190px] h-auto ml-4 md:ml-8 lg:ml-12 cursor-pointer flex-shrink-0 object-contain transition-transform duration-200 hover:scale-[1.02]"
            /></a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1 rounded-full border border-white/8 bg-white/[0.03] p-1">
              {navLinks.map(link => (
                <a
                  key={link.id}
                  href={navHref(link.id)}
                  className={`relative px-3.5 py-2 rounded-full text-[13px] font-medium tracking-wide transition-all duration-200 ${
                    activeSection === link.id
                      ? 'text-white bg-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]'
                      : 'text-gray-400 hover:text-white hover:bg-white/[0.06]'
                  }`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/industries"
                className="px-3.5 py-2 rounded-full text-[13px] font-medium tracking-wide text-gray-400 hover:text-white hover:bg-white/[0.06] transition-all duration-200"
              >
                Industries
              </a>
              <a
                href="/privacy-policy"
                className="px-3.5 py-2 rounded-full text-[13px] font-medium tracking-wide text-gray-400 hover:text-white hover:bg-white/[0.06] transition-all duration-200"
              >
                Legal
              </a>
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href="tel:0403448370"
                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-full border border-white/10 bg-white/[0.03] text-gray-300 hover:text-white hover:border-white/20 transition-all duration-200 text-sm font-medium whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#2563eb]/12">
                  <Phone className="w-3.5 h-3.5 text-[#3b82f6]" />
                </span>
                0403 448 370
              </a>
              <a
                href="/request-a-quote"
                className="group inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm px-5 py-3 transition-all duration-200 font-semibold tracking-wide rounded-full shadow-[0_8px_25px_rgba(37,99,235,0.28)] hover:shadow-[0_10px_30px_rgba(37,99,235,0.38)]"
              >
                Get a Quote
                <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-white p-2 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-[#0B0F14] lg:hidden flex flex-col pt-20 pb-10 px-4 sm:px-6 overflow-y-auto w-full max-w-[100vw] overflow-x-hidden">
          <nav className="flex flex-col gap-2 w-full">
            {navLinks.map(link => (
              <a
                key={link.id}
                href={navHref(link.id)}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-left text-base md:text-lg font-medium py-3 border-b border-white/5 transition-colors duration-200 w-full ${
                  activeSection === link.id ? 'text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <a
              href="/industries"
              className="text-left text-base md:text-lg font-medium py-3 border-b border-white/5 transition-colors duration-200 w-full text-gray-400 hover:text-white"
            >
              Industries
            </a>
            <a
              href="/privacy-policy"
              className="text-left text-base md:text-lg font-medium py-3 border-b border-white/5 transition-colors duration-200 w-full text-gray-400 hover:text-white"
            >
              Legal
            </a>

          <div className="mt-8 flex flex-col gap-4 w-full">
            <a
              href="/request-a-quote"
              className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-4 text-base font-semibold tracking-wide transition-all duration-300 rounded-sm"
            >
              Get a Quote
            </a>
            <a
              href="tel:0403448370"
              className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-[#2563eb]/50 text-gray-300 hover:text-white py-4 text-base font-medium transition-all duration-300 rounded-sm"
            >
              <Phone className="w-4 h-4 text-[#2563eb]" />
              0403 448 370
            </a>
          </div>
        </div>
      )}
    </>
  );
}
